import { HicapsConnectTerminal } from '@principle-theorem/principle-core';
import {
  BridgeDeviceStatus,
  IBridgeDevice,
  IHicapsConnectBridgeEdge,
  IHicapsConnectTerminalV2,
} from '@principle-theorem/principle-core/interfaces';
import { WithRef, isSameRef } from '@principle-theorem/shared';
import { compact, maxBy } from 'lodash';

export interface IHicapsConnectRouteCandidate {
  bridgeDevice: WithRef<IBridgeDevice>;
  edge: IHicapsConnectBridgeEdge;
}

export interface IHicapsConnectRouteResult {
  chosen: IHicapsConnectRouteCandidate;
  candidates: IHicapsConnectRouteCandidate[];
  usedFallback: boolean;
}

export class HicapsConnectV2Routing {
  /**
   * Key of `IHicapsConnectTerminalV2.bridges`. Deliberately the bridge doc id,
   * not `deviceId`: the doc id is guaranteed present, unique and immutable,
   * whereas `deviceId` is optional, unvalidated and can collide.
   */
  static bridgeEdgeKey(bridgeDevice: WithRef<IBridgeDevice>): string {
    return bridgeDevice.ref.id;
  }

  static findBridgeDevice(
    bridgeDevices: WithRef<IBridgeDevice>[],
    bridgeEdgeKey: string
  ): WithRef<IBridgeDevice> | undefined {
    return bridgeDevices.find(
      (bridgeDevice) =>
        HicapsConnectV2Routing.bridgeEdgeKey(bridgeDevice) === bridgeEdgeKey
    );
  }

  /**
   * A deleted or inactive bridge can still hold edges on a terminal document,
   * so both routing and sync must exclude it before treating it as reachable.
   */
  static isActiveBridgeDevice(bridgeDevice: WithRef<IBridgeDevice>): boolean {
    return (
      !bridgeDevice.deleted && bridgeDevice.status === BridgeDeviceStatus.Active
    );
  }

  static resolveRoute(
    terminal: WithRef<IHicapsConnectTerminalV2>,
    bridgeDevices: WithRef<IBridgeDevice>[]
  ): IHicapsConnectRouteResult | undefined {
    const candidates = HicapsConnectV2Routing._getRouteCandidates(
      terminal,
      bridgeDevices
    );
    const chosen =
      HicapsConnectV2Routing._choosePreferredCandidate(terminal, candidates) ??
      HicapsConnectV2Routing._getMostRecentCandidate(
        candidates.filter((item) => item.edge.isLocal)
      ) ??
      HicapsConnectV2Routing._getMostRecentCandidate(candidates);

    if (!chosen) {
      return;
    }

    return {
      chosen,
      candidates,
      usedFallback: HicapsConnectV2Routing._isPreferredBridgeFallback(
        terminal,
        chosen
      ),
    };
  }

  private static _getRouteCandidates(
    terminal: WithRef<IHicapsConnectTerminalV2>,
    bridgeDevices: WithRef<IBridgeDevice>[]
  ): IHicapsConnectRouteCandidate[] {
    return compact(
      Object.entries(terminal.bridges).map(([bridgeEdgeKey, edge]) =>
        HicapsConnectV2Routing._getRouteCandidate(
          bridgeEdgeKey,
          edge,
          bridgeDevices
        )
      )
    );
  }

  private static _getRouteCandidate(
    bridgeEdgeKey: string,
    edge: IHicapsConnectBridgeEdge,
    bridgeDevices: WithRef<IBridgeDevice>[]
  ): IHicapsConnectRouteCandidate | undefined {
    const bridgeDevice = HicapsConnectV2Routing.findBridgeDevice(
      bridgeDevices,
      bridgeEdgeKey
    );

    if (!bridgeDevice || !HicapsConnectTerminal.isBridgeEdgeActive(edge)) {
      return;
    }

    if (!HicapsConnectV2Routing.isActiveBridgeDevice(bridgeDevice)) {
      return;
    }

    return { bridgeDevice, edge };
  }

  private static _choosePreferredCandidate(
    terminal: WithRef<IHicapsConnectTerminalV2>,
    candidates: IHicapsConnectRouteCandidate[]
  ): IHicapsConnectRouteCandidate | undefined {
    if (!terminal.preferredBridge) {
      return;
    }

    return candidates.find((candidate) =>
      isSameRef(candidate.bridgeDevice.ref, terminal.preferredBridge)
    );
  }

  private static _getMostRecentCandidate(
    candidates: IHicapsConnectRouteCandidate[]
  ): IHicapsConnectRouteCandidate | undefined {
    return maxBy(candidates, (candidate) => candidate.edge.lastSeen.toMillis());
  }

  private static _isPreferredBridgeFallback(
    terminal: WithRef<IHicapsConnectTerminalV2>,
    chosen: IHicapsConnectRouteCandidate
  ): boolean {
    return (
      !!terminal.preferredBridge &&
      !isSameRef(terminal.preferredBridge, chosen.bridgeDevice.ref)
    );
  }
}
